import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { getSiteUrl, SITE_NAME, SITE_SOCIAL_HANDLE_X } from '../../data/toolRegistry';

interface SEOHeadProps {
  title: string;
  description: string;
  canonicalPath: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  ogImageAlt?: string;
  noindex?: boolean;
}

export function SEOHead({
  title,
  description,
  canonicalPath,
  ogImage = '/og/default.png',
  ogType = 'website',
  ogImageAlt,
  noindex = false,
}: SEOHeadProps) {
  const siteUrl = getSiteUrl();
  const canonicalUrl = `${siteUrl}${canonicalPath}`;
  const ogImageUrl = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;
  const resolvedOgImageAlt = ogImageAlt || `${title} preview image`;
  const isServer = typeof window === 'undefined';

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const existingCanonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;

    if (existingCanonical) {
      existingCanonical.setAttribute('href', canonicalUrl);
      if (!existingCanonical.hasAttribute('data-rh')) {
        existingCanonical.setAttribute('data-rh', 'true');
      }
      return;
    }

    const canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', canonicalUrl);
    canonical.setAttribute('data-rh', 'true');
    document.head.appendChild(canonical);
  }, [canonicalUrl]);

  return (
    <Helmet>
      {/* Primary Meta */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {isServer && <link rel="canonical" href={canonicalUrl} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={resolvedOgImageAlt} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SITE_SOCIAL_HANDLE_X} />
      <meta name="twitter:creator" content={SITE_SOCIAL_HANDLE_X} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:image:alt" content={resolvedOgImageAlt} />
    </Helmet>
  );
}
